import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore'
import { db } from './config'
import { COLLECTIONS } from '@/constants'
import type { Task, TaskInput, TaskStatus, ApiResponse, TaskTimelineEvent } from '@/types'
import { notificationService } from './notifications'

const getDb = () => {
  if (!db) throw new Error('Firebase is not configured.')
  return db
}

const fromFirestore = (id: string, data: Record<string, any>): Task => ({
  id,
  taskNumber: data.taskNumber ?? id.slice(0, 8).toUpperCase(),
  type: data.type,
  customType: data.customType,
  status: data.status,
  priority: data.priority,
  customerName: data.customerName,
  customerPhone: data.customerPhone,
  customerEmail: data.customerEmail,
  address: data.address,
  location: data.location,
  city: data.city,
  description: data.description,
  notes: data.notes,
  assignedTo: data.assignedTo,
  assignedEngineerName: data.assignedEngineerName,
  sitePhotos: data.sitePhotos ?? [],
  sitePhotoUploadedAt: (data.sitePhotoUploadedAt as Timestamp)?.toDate?.(),
  adminApproved: data.adminApproved,
  adminApprovedAt: (data.adminApprovedAt as Timestamp)?.toDate?.(),
  adminApprovedBy: data.adminApprovedBy,
  customerApproved: data.customerApproved,
  customerFeedback: data.customerFeedback,
  customerRating: data.customerRating,
  wouldRecommend: data.wouldRecommend,
  feedbackCategories: data.feedbackCategories,
  feasibilityStatus: data.feasibilityStatus,
  feasibilityComments: data.feasibilityComments,
  feasibilityWorkflowRequired: data.feasibilityWorkflowRequired,
  feasibilityLatitude: data.feasibilityLatitude,
  feasibilityLongitude: data.feasibilityLongitude,
  feasibilityApproved: data.feasibilityApproved,
  speedTestDownload: data.speedTestDownload !== undefined ? Number(data.speedTestDownload) : undefined,
  speedTestUpload: data.speedTestUpload !== undefined ? Number(data.speedTestUpload) : undefined,
  speedTestPing: data.speedTestPing !== undefined ? Number(data.speedTestPing) : undefined,
  speedTestIsp: data.speedTestIsp,
  speedTestIp: data.speedTestIp,
  speedTestTimestamp: (data.speedTestTimestamp as Timestamp)?.toDate?.(),
  speedTestLatitude: data.speedTestLatitude,
  speedTestLongitude: data.speedTestLongitude,
  serviceNumber: data.serviceNumber,
  bbUserId: data.bbUserId,
  oltIp: data.oltIp,
  vlan: data.vlan !== undefined ? data.vlan : null,
  port: data.port !== undefined ? data.port : null,
  category: data.category,
  exchangeCode: data.exchangeCode,
  subServiceType: data.subServiceType,
  subscriptionPlan: data.subscriptionPlan,
  planPeriod: data.planPeriod,
  billingAccountNo: data.billingAccountNo !== undefined ? Number(data.billingAccountNo) : undefined,
  fmc: data.fmc !== undefined ? Number(data.fmc) : undefined,
  voipPassword: data.voipPassword,
  activationDate: data.activationDate,
  mismatchFlag: data.mismatchFlag,
  mismatchComments: data.mismatchComments,
  verificationTicked: data.verificationTicked,
  provisioningStage: data.provisioningStage,
  provisioningChecklist: data.provisioningChecklist ?? [],
  provisioningPlan: data.provisioningPlan,
  provisioningPlanPeriod: data.provisioningPlanPeriod,
  provisioningFmc: data.provisioningFmc !== undefined ? Number(data.provisioningFmc) : undefined,
  kycUrls: data.kycUrls ?? [],
  kycUploadedAt: (data.kycUploadedAt as Timestamp)?.toDate?.(),
  bsnlRegisteredAt: (data.bsnlRegisteredAt as Timestamp)?.toDate?.(),
  provisionedAt: (data.provisionedAt as Timestamp)?.toDate?.(),
  provisionedName: data.provisionedName,
  provisionedPhone: data.provisionedPhone,
  provisionedEmail: data.provisionedEmail,
  provisionedAddress: data.provisionedAddress,
  provisionedCity: data.provisionedCity,
  provisionedLocation: data.provisionedLocation,
  installationPhase: data.installationPhase,
  installationEngineerId: data.installationEngineerId,
  installationEngineerName: data.installationEngineerName,
  installationAssignedAt: (data.installationAssignedAt as Timestamp)?.toDate?.(),
  installationCompletedAt: (data.installationCompletedAt as Timestamp)?.toDate?.(),
  installationDataLoaded: data.installationDataLoaded,
  installationVerification: data.installationVerification,
  createdBy: data.createdBy,
  createdAt: (data.createdAt as Timestamp)?.toDate?.() ?? new Date(),
  updatedAt: (data.updatedAt as Timestamp)?.toDate?.() ?? new Date(),
  closedAt: (data.closedAt as Timestamp)?.toDate?.()
})

const generateTaskNumber = () => {
  const now = new Date()
  const yy = String(now.getFullYear()).slice(2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const rand = Math.floor(1000 + Math.random() * 9000)
  return `TSK-${yy}${mm}${dd}-${rand}`
}

// ── Comment types ─────────────────────────────────────────────────────────────
export interface TaskComment {
  id: string
  taskId: string
  authorId: string
  authorName: string
  authorRole: 'admin' | 'engineer'
  text: string
  taggedAdmin: boolean
  createdAt: Date
}

export const taskService = {
  async list(): Promise<ApiResponse<Task[]>> {
    try {
      const q = query(collection(getDb(), COLLECTIONS.TASKS), orderBy('createdAt', 'desc'))
      const snap = await getDocs(q)
      return { success: true, data: snap.docs.map(d => fromFirestore(d.id, d.data())) }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch tasks' }
    }
  },

  async get(id: string): Promise<ApiResponse<Task>> {
    try {
      const snap = await getDoc(doc(getDb(), COLLECTIONS.TASKS, id))
      if (!snap.exists()) return { success: false, error: 'Task not found' }
      return { success: true, data: fromFirestore(snap.id, snap.data()) }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch task' }
    }
  },

  async listByAssignee(engineerId: string, engineerDocId?: string): Promise<ApiResponse<Task[]>> {
    try {
      // Build the set of valid IDs for this engineer
      const validIds = [engineerId, engineerDocId].filter(Boolean) as string[]

      const q = query(
        collection(getDb(), COLLECTIONS.TASKS),
        where('assignedTo', '==', engineerId)
      )
      const snap = await getDocs(q)
      const tasks1 = snap.docs.map(d => fromFirestore(d.id, d.data()))

      let tasks2: Task[] = []
      if (engineerDocId && engineerDocId !== engineerId) {
        const q2 = query(
          collection(getDb(), COLLECTIONS.TASKS),
          where('assignedTo', '==', engineerDocId)
        )
        const snap2 = await getDocs(q2)
        tasks2 = snap2.docs.map(d => fromFirestore(d.id, d.data()))
      }

      const all = [...tasks1, ...tasks2]
      // Strict ownership filter — only tasks whose assignedTo is one of this engineer's IDs
      const owned = all.filter(t => t.assignedTo && validIds.includes(t.assignedTo))
      const unique = owned.filter((t, i) => owned.findIndex(x => x.id === t.id) === i)
      unique.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

      return { success: true, data: unique }
    } catch (error) {
      console.error('[tasks] listByAssignee failed:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch tasks' }
    }
  },

  async listUnassigned(): Promise<ApiResponse<Task[]>> {
    try {
      const q = query(
        collection(getDb(), COLLECTIONS.TASKS),
        where('status', '==', 'open')
      )
      const snap = await getDocs(q)
      const data = snap.docs.map(d => fromFirestore(d.id, d.data()))
      data.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      return { success: true, data }
    } catch (error) {
      console.error('[tasks] listUnassigned failed:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch tasks' }
    }
  },

  async listByStatus(status: TaskStatus): Promise<ApiResponse<Task[]>> {
    try {
      const q = query(
        collection(getDb(), COLLECTIONS.TASKS),
        where('status', '==', status)
      )
      const snap = await getDocs(q)
      const data = snap.docs.map(d => fromFirestore(d.id, d.data()))
      data.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      return { success: true, data }
    } catch (error) {
      console.error('[tasks] listByStatus failed:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch tasks' }
    }
  },

  async create(input: TaskInput): Promise<ApiResponse<Task>> {
    try {
      const taskNumber = generateTaskNumber()
      // Firestore rejects undefined values — strip them before writing
      const clean = Object.fromEntries(
        Object.entries({ ...input, taskNumber })
          .filter(([, v]) => v !== undefined)
      )
      const ref = await addDoc(collection(getDb(), COLLECTIONS.TASKS), {
        ...clean,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Log timeline event
      await taskService.addTimelineEvent(ref.id, {
        status: input.status || 'open',
        title: 'Task Created',
        description: input.description || 'Task registered in the system',
        updatedBy: 'Admin',
      })

      const snap = await getDoc(ref)
      return { success: true, data: fromFirestore(snap.id, snap.data()!) }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create task' }
    }
  },

  async assignEngineer(id: string, engineerId: string, engineerName: string): Promise<ApiResponse<void>> {
    try {
      // Fetch task for notification payload before updating
      const taskSnap = await getDoc(doc(getDb(), COLLECTIONS.TASKS, id))
      const taskData = taskSnap.exists() ? taskSnap.data() : null

      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), {
        assignedTo: engineerId,
        assignedEngineerName: engineerName,
        status: 'assigned',
        updatedAt: serverTimestamp(),
      })

      // Log timeline event
      await taskService.addTimelineEvent(id, {
        status: 'assigned',
        title: `Assigned to ${engineerName}`,
        description: 'Field engineer assigned to complete the work',
        updatedBy: 'Admin',
      })

      // Fire-and-forget notification (non-blocking)
      if (taskData) {
        notificationService.sendAssignmentNotification({
          engineerId,
          engineerName,
          taskId:       id,
          taskNumber:   taskData.taskNumber ?? id.slice(0, 8).toUpperCase(),
          customerName: taskData.customerName ?? 'Customer',
          taskType:     taskData.type ?? 'task',
          address:      `${taskData.address ?? ''}${taskData.city ? ', ' + taskData.city : ''}`,
          priority:     taskData.priority ?? 'medium',
        }).catch(err => console.warn('[FCM] Assignment notification failed:', err))
      }

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to assign engineer' }
    }
  },

  async attachSitePhotos(id: string, photoUrls: string[]): Promise<ApiResponse<void>> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), {
        sitePhotos: photoUrls,
        sitePhotoUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to save photos' }
    }
  },

  async submitFeasibilityStudy(id: string, payload: {
    feasibilityStatus: 'feasible' | 'not_feasible'
    feasibilityComments: string
    feasibilityWorkflowRequired: string[]
    latitude?: number
    longitude?: number
  }, engineerName?: string): Promise<ApiResponse<void>> {
    try {
      const taskDoc = await getDoc(doc(getDb(), COLLECTIONS.TASKS, id))
      const taskData = taskDoc.exists() ? taskDoc.data() : null
      const taskNumber = taskData?.taskNumber || id.slice(0, 8).toUpperCase()

      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), {
        feasibilityStatus: payload.feasibilityStatus,
        feasibilityComments: payload.feasibilityComments || '',
        feasibilityWorkflowRequired: payload.feasibilityWorkflowRequired ?? [],
        feasibilityLatitude: payload.latitude ?? null,
        feasibilityLongitude: payload.longitude ?? null,
        status: 'pending_approval' as const,
        updatedAt: serverTimestamp(),
      })

      // Log timeline event
      await taskService.addTimelineEvent(id, {
        status: 'pending_approval',
        title: `Feasibility: ${payload.feasibilityStatus === 'feasible' ? '⚡ FEASIBLE' : '❌ NOT FEASIBLE'}`,
        description: payload.feasibilityComments || 'Feasibility study completed by engineer.',
        updatedBy: engineerName || 'Engineer',
      })

      // Notify admins
      await notificationService.sendFeasibilitySubmittedNotification({
        engineerName: engineerName || 'Engineer',
        taskId: id,
        taskNumber,
        feasibilityStatus: payload.feasibilityStatus,
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Feasibility study submission failed' }
    }
  },

  async recordSpeedTest(id: string, payload: {
    download: number
    upload: number
    ping: number
    isp: string
    ip: string
    latitude?: number
    longitude?: number
  }): Promise<ApiResponse<void>> {
    try {
      const updates: Record<string, any> = {
        speedTestDownload: payload.download,
        speedTestUpload: payload.upload,
        speedTestPing: payload.ping,
        speedTestIsp: payload.isp,
        speedTestIp: payload.ip,
        speedTestTimestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      if (payload.latitude !== undefined) updates.speedTestLatitude = payload.latitude
      if (payload.longitude !== undefined) updates.speedTestLongitude = payload.longitude
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), updates)
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to record speed test' }
    }
  },

  async approveTask(id: string, approvedBy: string, approvedByName?: string): Promise<ApiResponse<void>> {
    try {
      const taskDoc = await getDoc(doc(getDb(), COLLECTIONS.TASKS, id))
      const taskData = taskDoc.exists() ? taskDoc.data() : null
      const isEnquiry = taskData?.type === 'enquiry'

      const status: TaskStatus = isEnquiry ? 'closed' : 'pending_feedback'

      const updates: Record<string, any> = {
        adminApproved: true,
        adminApprovedAt: serverTimestamp(),
        adminApprovedBy: approvedBy,
        status,
        updatedAt: serverTimestamp(),
      }

      if (status === 'closed') {
        updates.closedAt = serverTimestamp()
      }

      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), updates)

      // Log timeline event
      await taskService.addTimelineEvent(id, {
        status,
        title: isEnquiry ? 'Approved & Closed by Admin' : 'Approved by Admin',
        description: isEnquiry
          ? 'Enquiry approved and successfully closed.'
          : 'Awaiting customer feedback to close the task',
        updatedBy: approvedByName || 'Admin',
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Approval failed' }
    }
  },

  async submitFeedback(id: string, payload: {
    rating: number
    feedback: string
    wouldRecommend: boolean
    categories: { serviceQuality: number; responseTime: number; communication: number; workmanship: number }
  }, customerName?: string): Promise<ApiResponse<void>> {
    try {
      const clean = Object.fromEntries(
        Object.entries({
          customerApproved: true,
          customerRating: payload.rating,
          customerFeedback: payload.feedback || undefined,
          wouldRecommend: payload.wouldRecommend,
          feedbackCategories: payload.categories,
          status: 'closed' as const,
          closedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }).filter(([, v]) => v !== undefined)
      )
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), clean)

      // Log timeline event
      await taskService.addTimelineEvent(id, {
        status: 'closed',
        title: 'Customer Feedback Submitted',
        description: `Rating: ${payload.rating}/5. ${payload.feedback ? `"${payload.feedback}"` : ''}`,
        updatedBy: customerName || 'Customer',
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Feedback submission failed' }
    }
  },

  async updateStatus(id: string, status: TaskStatus, updatedBy?: string): Promise<ApiResponse<void>> {
    try {
      const updates: Record<string, any> = { status, updatedAt: serverTimestamp() }
      if (status === 'closed') updates.closedAt = serverTimestamp()
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), updates)

      // Log timeline event
      const STATUS_LABELS: Record<TaskStatus, string> = {
        open: 'Open',
        assigned: 'Assigned',
        in_progress: 'In Progress',
        site_visit: 'Site Visit',
        work_in_progress: 'Work in Progress',
        pending_approval: 'Pending Approval',
        pending_feedback: 'Pending Feedback',
        closed: 'Closed',
        cancelled: 'Cancelled',
      }
      await taskService.addTimelineEvent(id, {
        status,
        title: `Status: ${STATUS_LABELS[status] || status}`,
        description: `Task status updated to ${STATUS_LABELS[status] || status}`,
        updatedBy: updatedBy || 'System',
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update task' }
    }
  },

  async update(id: string, data: Partial<TaskInput>): Promise<ApiResponse<void>> {
    try {
      const clean = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      )
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), { ...clean, updatedAt: serverTimestamp() })
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update task' }
    }
  },

  async remove(id: string): Promise<ApiResponse<void>> {
    try {
      await deleteDoc(doc(getDb(), COLLECTIONS.TASKS, id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete task' }
    }
  },

  async updateVerificationTicks(id: string, ticks: Record<string, boolean>): Promise<ApiResponse<void>> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), {
        verificationTicked: ticks,
        updatedAt: serverTimestamp()
      })
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update ticks' }
    }
  },

  async reportMismatch(id: string, comments: string): Promise<ApiResponse<void>> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, id), {
        mismatchFlag: true,
        mismatchComments: comments,
        updatedAt: serverTimestamp()
      })

      // Fetch task status
      let currentStatus: TaskStatus = 'in_progress'
      try {
        const snap = await getDoc(doc(getDb(), COLLECTIONS.TASKS, id))
        if (snap.exists()) {
          currentStatus = snap.data().status as TaskStatus
        }
      } catch (e) {
        console.warn('Failed to fetch status for timeline:', e)
      }

      // Log timeline event
      await taskService.addTimelineEvent(id, {
        status: currentStatus,
        title: 'Mismatch Reported',
        description: comments,
        updatedBy: 'Engineer',
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to report mismatch' }
    }
  },

  async seedSampleTasks(createdBy: string): Promise<ApiResponse<number>> {
    try {
      const samples: Omit<TaskInput, never>[] = [
        {
          type: 'new_connection',
          status: 'open',
          priority: 'high',
          customerName: 'Ramesh Kumar',
          customerPhone: '9876543210',
          customerEmail: 'ramesh.kumar@gmail.com',
          address: 'Flat 4B, Sunrise Apartments, MG Road',
          location: 'Near City Mall',
          city: 'Kozhikode',
          description: 'Customer requesting new FTTH broadband connection. ONU installation required.',
          createdBy,
        },
        {
          type: 'support',
          status: 'in_progress',
          priority: 'urgent' as const,
          customerName: 'Priya Nair',
          customerPhone: '9845123456',
          customerEmail: 'priya.nair@yahoo.com',
          address: '12, Seaview Colony, Beach Road',
          location: 'Behind Police Station',
          city: 'Thrissur',
          description: 'Intermittent connectivity drops since 2 days. Likely fibre splice issue.',
          notes: 'Customer has complained twice. Escalate if not resolved today.',
          createdBy,
        },
        {
          type: 'enquiry',
          status: 'open',
          priority: 'medium',
          customerName: 'Mohammed Salim',
          customerPhone: '9567890123',
          address: '88, Green Park Layout, 3rd Cross',
          location: 'Near Bus Stand',
          city: 'Malappuram',
          description: 'Enquiry about 100 Mbps plan availability in the area.',
          createdBy,
        },
      ]

      const col = collection(getDb(), COLLECTIONS.TASKS)
      let count = 0
      for (const sample of samples) {
        const clean = Object.fromEntries(
          Object.entries({ ...sample, taskNumber: generateTaskNumber() })
            .filter(([, v]) => v !== undefined)
        )
        await addDoc(col, {
          ...clean,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        count++
      }
      return { success: true, data: count }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Seed failed' }
    }
  },

  async addComment(taskId: string, comment: {
    authorId: string
    authorName: string
    authorRole: 'admin' | 'engineer'
    text: string
    taggedAdmin: boolean
  }): Promise<ApiResponse<TaskComment>> {
    try {
      const ref = await addDoc(
        collection(getDb(), COLLECTIONS.TASKS, taskId, 'comments'),
        { ...comment, taskId, createdAt: serverTimestamp() }
      )
      const snap = await getDoc(ref)
      const d = snap.data()!
      return {
        success: true,
        data: {
          id: snap.id,
          taskId,
          authorId: d.authorId,
          authorName: d.authorName,
          authorRole: d.authorRole,
          text: d.text,
          taggedAdmin: d.taggedAdmin,
          createdAt: (d.createdAt as Timestamp)?.toDate?.() ?? new Date(),
        }
      }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to add comment' }
    }
  },

  async updateProvisioningStage(taskId: string, stage: string, extra?: Record<string, unknown>): Promise<ApiResponse<void>> {
    try {
      const updates: Record<string, unknown> = { provisioningStage: stage, updatedAt: serverTimestamp(), ...(extra ?? {}) }
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, taskId), updates)

      // Add timeline event for provisioning stage changes
      const stageLabels: Record<string, string> = {
        customer_confirmed: 'Customer Contacted',
        plan_selected: 'Plan Selected',
        bsnl_registered: 'BSNL Registration Complete',
        provisioned: 'Customer Provisioned',
      }
      const label = stageLabels[stage] || stage
      await this.addTimelineEvent(taskId, {
        status: 'in_progress',
        title: `Provisioning: ${label}`,
        description: `Provisioning stage updated to ${label}`,
        updatedBy: 'Admin',
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update provisioning stage' }
    }
  },

  async updateProvisioningChecklist(taskId: string, checklist: string[], extra?: Record<string, unknown>): Promise<ApiResponse<void>> {
    try {
      const updates: Record<string, unknown> = { provisioningChecklist: checklist, updatedAt: serverTimestamp(), ...(extra ?? {}) }
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, taskId), updates)
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update provisioning checklist' }
    }
  },

  async attachKycDocs(taskId: string, urls: string[]): Promise<ApiResponse<void>> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, taskId), {
        kycUrls: urls,
        kycUploadedAt: serverTimestamp(),
        provisioningStage: 'kyc_uploaded',
        updatedAt: serverTimestamp(),
      })
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to attach KYC docs' }
    }
  },

  async updateInstallationPhase(taskId: string, phase: string, extra?: Record<string, unknown>): Promise<ApiResponse<void>> {
    try {
      const updates: Record<string, unknown> = { installationPhase: phase, updatedAt: serverTimestamp(), ...(extra ?? {}) }
      if (phase === 'completed') updates.installationCompletedAt = serverTimestamp()
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, taskId), updates)

      // Add timeline event for installation phase changes
      const phaseLabels: Record<string, string> = {
        assigned: 'Installation Assigned',
        in_progress: 'Installation In Progress',
        site_visit: 'Site Visit Scheduled',
        work_in_progress: 'Installation Work Started',
        pending_approval: 'Installation Pending Approval',
        completed: 'Installation Completed',
      }
      const label = phaseLabels[phase] || phase
      await this.addTimelineEvent(taskId, {
        status: 'in_progress',
        title: `Installation: ${label}`,
        description: `Installation phase updated to ${label}`,
        updatedBy: 'Admin',
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update installation phase' }
    }
  },

  async updateInstallationVerification(taskId: string, data: { dataLoaded?: boolean; verification?: Record<string, boolean> }): Promise<ApiResponse<void>> {
    try {
      const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
      if (data.dataLoaded !== undefined) updates.installationDataLoaded = data.dataLoaded
      if (data.verification !== undefined) updates.installationVerification = data.verification
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, taskId), updates)
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update installation verification' }
    }
  },

  async assignInstallationEngineer(taskId: string, engineerId: string, engineerName: string): Promise<ApiResponse<void>> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.TASKS, taskId), {
        installationEngineerId: engineerId,
        installationEngineerName: engineerName,
        installationPhase: 'assigned',
        installationAssignedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      notificationService.sendAssignmentNotification({
        engineerId,
        engineerName,
        taskId,
        taskNumber: '',
        customerName: '',
        taskType: 'new_connection',
        address: '',
        priority: 'medium',
      }).catch(() => {})
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to assign installation engineer' }
    }
  },

  async listComments(taskId: string): Promise<ApiResponse<TaskComment[]>> {
    try {
      const q = query(
        collection(getDb(), COLLECTIONS.TASKS, taskId, 'comments'),
        orderBy('createdAt', 'asc')
      )
      const snap = await getDocs(q)
      const comments: TaskComment[] = snap.docs.map(d => ({
        id: d.id,
        taskId,
        authorId: d.data().authorId,
        authorName: d.data().authorName,
        authorRole: d.data().authorRole,
        text: d.data().text,
        taggedAdmin: d.data().taggedAdmin,
        createdAt: (d.data().createdAt as Timestamp)?.toDate?.() ?? new Date(),
      }))
      return { success: true, data: comments }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load comments' }
    }
  },

  async addTimelineEvent(taskId: string, event: {
    status: TaskStatus
    title: string
    description?: string
    updatedBy: string
  }): Promise<ApiResponse<TaskTimelineEvent>> {
    try {
      const ref = await addDoc(
        collection(getDb(), COLLECTIONS.TASKS, taskId, 'timeline'),
        { ...event, taskId, createdAt: serverTimestamp() }
      )
      const snap = await getDoc(ref)
      const d = snap.data()!
      return {
        success: true,
        data: {
          id: snap.id,
          taskId,
          status: d.status,
          title: d.title,
          description: d.description,
          updatedBy: d.updatedBy,
          createdAt: (d.createdAt as Timestamp)?.toDate?.() ?? new Date(),
        }
      }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to add timeline event' }
    }
  },

  async deleteAllTasks(): Promise<ApiResponse<number>> {
    try {
      const snap = await getDocs(collection(getDb(), COLLECTIONS.TASKS))
      let count = 0
      for (const taskDoc of snap.docs) {
        // Delete sub-collections first
        const [commentsSnap, timelineSnap] = await Promise.all([
          getDocs(collection(getDb(), COLLECTIONS.TASKS, taskDoc.id, 'comments')),
          getDocs(collection(getDb(), COLLECTIONS.TASKS, taskDoc.id, 'timeline')),
        ])
        await Promise.all([
          ...commentsSnap.docs.map(d => deleteDoc(d.ref)),
          ...timelineSnap.docs.map(d => deleteDoc(d.ref)),
        ])
        await deleteDoc(taskDoc.ref)
        count++
      }
      return { success: true, data: count }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete tasks' }
    }
  },

  async listTimelineEvents(taskId: string): Promise<ApiResponse<TaskTimelineEvent[]>> {
    try {
      const q = query(
        collection(getDb(), COLLECTIONS.TASKS, taskId, 'timeline'),
        orderBy('createdAt', 'asc')
      )
      const snap = await getDocs(q)
      const events: TaskTimelineEvent[] = snap.docs.map(d => ({
        id: d.id,
        taskId,
        status: d.data().status,
        title: d.data().title,
        description: d.data().description,
        updatedBy: d.data().updatedBy,
        createdAt: (d.data().createdAt as Timestamp)?.toDate?.() ?? new Date(),
      }))
      return { success: true, data: events }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load timeline events' }
    }
  },
}
